
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { BalanceSheetEntry, ACCOUNT_CATEGORIES } from '@/lib/balance-sheet-types';

interface BalanceSheetEntryFormProps {
  onEntryAdded: () => void;
  editingEntry?: BalanceSheetEntry | null;
  onEditComplete?: () => void;
}

export function BalanceSheetEntryForm({ onEntryAdded, editingEntry, onEditComplete }: BalanceSheetEntryFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    account_name: editingEntry?.account_name || '',
    account_type: editingEntry?.account_type || 'asset' as 'asset' | 'liability' | 'equity',
    account_category: editingEntry?.account_category || '',
    balance: editingEntry?.balance || 0,
    period_end_date: editingEntry?.period_end_date || new Date().toISOString().split('T')[0],
    notes: editingEntry?.notes || ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to add balance sheet entries.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const entryData = {
        ...formData,
        user_id: user.id,
      };

      let result;
      if (editingEntry) {
        result = await supabase
          .from('balance_sheet_entries')
          .update(entryData)
          .eq('id', editingEntry.id);
      } else {
        result = await supabase
          .from('balance_sheet_entries')
          .insert([entryData]);
      }

      if (result.error) throw result.error;

      toast({
        title: "Success",
        description: `Balance sheet entry ${editingEntry ? 'updated' : 'added'} successfully.`,
      });

      if (editingEntry && onEditComplete) {
        onEditComplete();
      } else {
        setFormData({
          account_name: '',
          account_type: 'asset',
          account_category: '',
          balance: 0,
          period_end_date: new Date().toISOString().split('T')[0],
          notes: ''
        });
      }
      
      onEntryAdded();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const availableCategories = ACCOUNT_CATEGORIES[formData.account_type] || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{editingEntry ? 'Edit' : 'Add New'} Balance Sheet Entry</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="account_name">Account Name</Label>
              <Input
                id="account_name"
                value={formData.account_name}
                onChange={(e) => setFormData(prev => ({ ...prev, account_name: e.target.value }))}
                placeholder="e.g., Cash, Accounts Receivable"
                required
              />
            </div>

            <div>
              <Label htmlFor="account_type">Account Type</Label>
              <Select
                value={formData.account_type}
                onValueChange={(value: 'asset' | 'liability' | 'equity') => 
                  setFormData(prev => ({ ...prev, account_type: value, account_category: '' }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select account type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asset">Asset</SelectItem>
                  <SelectItem value="liability">Liability</SelectItem>
                  <SelectItem value="equity">Equity</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="account_category">Account Category</Label>
              <Select
                value={formData.account_category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, account_category: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {availableCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="balance">Balance</Label>
              <Input
                id="balance"
                type="number"
                step="0.01"
                value={formData.balance}
                onChange={(e) => setFormData(prev => ({ ...prev, balance: parseFloat(e.target.value) || 0 }))}
                required
              />
            </div>

            <div>
              <Label htmlFor="period_end_date">Period End Date</Label>
              <Input
                id="period_end_date"
                type="date"
                value={formData.period_end_date}
                onChange={(e) => setFormData(prev => ({ ...prev, period_end_date: e.target.value }))}
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes about this entry..."
              rows={3}
            />
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : editingEntry ? 'Update Entry' : 'Add Entry'}
            </Button>
            {editingEntry && onEditComplete && (
              <Button type="button" variant="outline" onClick={onEditComplete}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
